import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { enrollInQuiz } from '@actions/quizTaking';
import { getQuizDetails, getSelectedLanguage } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { TEST_STARTED } from '@constants/segment';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToIconic,
  getIsSubscribedToLite
} from '@selectors/subscription';
import { getIsGTPGoal, getIsTestRevampGoal } from '@api/selectors/constants';
import useConstants from '@api/hooks/useConstants';
import useQuizSegmentAnalytics from './useQuizSegmentAnalytics';

const useEnroll = () => {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { quizUID } = query;

  const quizDetails = useSelectorWithShallowEqual(getQuizDetails(quizUID));
  const isFree = quizDetails.collection?.programme?.is_special;
  const selectedLanguage = useSelectorWithShallowEqual(getSelectedLanguage);
  const isGTP = useConstants(getIsGTPGoal(quizDetails?.goal_uid));
  const isRevampedTestVersion2 = useConstants(
    getIsTestRevampGoal(quizDetails.goal_uid)
  );

  const segmentAnalytics = useQuizSegmentAnalytics();

  const sendAnalyticsOnEnroll = () => {
    const defaultProps = {
      is_reattempt: !!quizDetails?.previous_sessions?.length,
      language: selectedLanguage.display_name,
      is_sellable: quizDetails.is_sellable
    };
    if (isGTP) {
      defaultProps.test_eligibility_type = isFree ? 'Free Test' : 'Plus Test';
    }
    segmentAnalytics(TEST_STARTED, defaultProps);
  };

  const isIconicUser = useSelector(
    getIsSubscribedToIconic(quizDetails.goal_uid)
  );
  const isPlusUser = useSelector(getIsSubscribedToGoal(quizDetails.goal_uid));
  const isLiteUser = useSelector(getIsSubscribedToLite(quizDetails.goal_uid));

  const checkIfUserHasSubscription = async (skipCheck = false) => {
    if (skipCheck) {
      return null;
    }

    const isIconic = quizDetails.collection.programme.is_iconic;
    const isSpecial = quizDetails.collection.programme.is_special;
    const isSubscribed = isIconic ? isIconicUser : isPlusUser || isLiteUser;

    let message = `You don't have an active subscription in this goal. Get ${
      isIconic ? 'Iconic ' : ''
    }subscription to attempt this test.`;

    if (isIconic && !isIconicUser && isPlusUser) {
      message =
        'You need to upgrade to iconic subscription to attempt this test.';
    }

    if (!isSubscribed && !isSpecial) {
      throw new Error(message);
    }
  };

  const enrollUserInQuiz = async () => {
    const { error, result } = await dispatch(
      enrollInQuiz(quizUID, selectedLanguage.code, isRevampedTestVersion2)
    );

    if (error) {
      throw new Error(
        error.message || 'Unable to start the test. Please try again!'
      );
    }

    return result;
  };

  const onEnroll = async () => {
    if (!selectedLanguage?.code) {
      throw new Error('Please select the language. Please try again!');
    }

    await enrollUserInQuiz();
    sendAnalyticsOnEnroll();
  };

  return {
    onEnroll,
    checkIfUserHasSubscription
  };
};

export default useEnroll;
